const STORAGE_KEY = 'kyc_studio_reviews_v2';
const LEGACY_KEYS = ['kyc_studio_reviews_v1', 'kyc-content-reviews', 'reviews', 'reviewStore'];

function safeParse(value) {
  try { return value ? JSON.parse(value) : null; } catch { return null; }
}

function normaliseReviews(value) {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return {};
  const candidate = value.reviews && typeof value.reviews === 'object' ? value.reviews : value;
  return Object.fromEntries(Object.entries(candidate).filter(([key, review]) =>
    key && review && typeof review === 'object' && !Array.isArray(review)
  ));
}

function migrateLegacyReviews() {
  for (const key of LEGACY_KEYS) {
    const parsed = safeParse(localStorage.getItem(key));
    const reviews = normaliseReviews(parsed);
    if (Object.keys(reviews).length) {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(reviews));
      return reviews;
    }
  }
  return {};
}

export function loadReviews() {
  const stored = normaliseReviews(safeParse(localStorage.getItem(STORAGE_KEY)));
  return Object.keys(stored).length ? stored : migrateLegacyReviews();
}

export function reviewRecordKey({ roleKey, scope = 'national', trustId = null, assignmentId = 'default' }) {
  if (!roleKey) throw new Error('A role key is required for a review record.');
  const scopeKey = scope === 'trust' ? `trust:${trustId || 'unknown'}` : 'national';
  return `${scopeKey}:${assignmentId}:${roleKey}`;
}

export function saveRoleReview(roleKey, review, { scope = 'national', trustId = null, assignmentId = 'default' } = {}) {
  const all = loadReviews();
  all[roleKey] = {
    ...all[roleKey],
    ...review,
    reviewScope: scope,
    trustId: scope === 'trust' ? trustId : null,
    assignmentId,
    recordKey: reviewRecordKey({ roleKey, scope, trustId, assignmentId }),
    updatedAt: new Date().toISOString()
  };
  localStorage.setItem(STORAGE_KEY, JSON.stringify(all));
  return all[roleKey];
}

export function importReviews(payload, { mode = 'merge' } = {}) {
  const incoming = normaliseReviews(payload);
  if (!Object.keys(incoming).length) throw new Error('The selected file does not contain any valid role reviews.');
  const current = mode === 'replace' ? {} : loadReviews();
  const merged = { ...current, ...incoming };
  localStorage.setItem(STORAGE_KEY, JSON.stringify(merged));
  return { reviews: merged, importedCount: Object.keys(incoming).length, totalCount: Object.keys(merged).length };
}

export function replaceLocalReviews(reviews) {
  const normalised = normaliseReviews(reviews);
  localStorage.setItem(STORAGE_KEY, JSON.stringify(normalised));
  return normalised;
}

export function mergeReviewsByUpdatedAt(primary, secondary) {
  const result = { ...normaliseReviews(primary) };
  for (const [roleKey, review] of Object.entries(normaliseReviews(secondary))) {
    const existing = result[roleKey];
    const existingTime = Date.parse(existing?.updatedAt || 0) || 0;
    const incomingTime = Date.parse(review?.updatedAt || 0) || 0;
    if (!existing || incomingTime >= existingTime) result[roleKey] = review;
  }
  return result;
}

export function exportReviews(libraryVersion) {
  const payload = {
    schemaVersion: 2,
    sourceLibrarySchemaVersion: libraryVersion,
    exportedAt: new Date().toISOString(),
    reviews: loadReviews()
  };
  downloadJson(payload, `kyc-content-reviews-${new Date().toISOString().slice(0, 10)}.json`);
}

export function downloadReviewBackup(libraryVersion) {
  exportReviews(libraryVersion);
}

function downloadJson(payload, filename) {
  const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  anchor.click();
  setTimeout(() => URL.revokeObjectURL(url), 0);
}
